import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { getFormularioBySlug, submitFormulario } from "@/lib/api";
import { loadRecaptchaScript, getRecaptchaToken, getSiteKey } from "@/lib/recaptcha";
import { ChevronLeft, ChevronRight, Loader2, Send, X } from "lucide-react";

const EMOJI_PREDETERMINADO = "📄";
import { toast } from "sonner";
import { useIsTouch } from "@/hooks/use-mobile";

function readFileAsDataUrlAny(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Error al leer"));
    r.readAsDataURL(file);
  });
}

function flattenBlocks(blocks) {
  const list = [];
  const walk = (arr) => {
    for (const b of arr || []) {
      if (!b || typeof b !== "object") continue;
      list.push(b);
      if (b.tipo === "columnas") {
        const cols = Array.isArray(b.columnas_bloques) ? b.columnas_bloques : [];
        cols.forEach((col) => walk(Array.isArray(col) ? col : []));
      }
    }
  };
  walk(Array.isArray(blocks) ? blocks : []);
  return list;
}

const OBLIGATORIABLES = ["input", "area_texto", "opciones_checkbox", "desplegable", "archivo"];

function pagesOfBlocks(bloques) {
  const out = [];
  let current = [];
  for (const b of bloques) {
    if (b.tipo === "paginacion") {
      if (current.length) out.push(current);
      current = [];
    } else current.push(b);
  }
  if (current.length) out.push(current);
  return out.length ? out : [[]];
}

function isAnswered(b, value) {
  if (b.tipo === "opciones_checkbox" || b.tipo === "archivo") {
    try {
      const parsed = JSON.parse(value || "[]");
      return Array.isArray(parsed) && parsed.length > 0;
    } catch (_) {
      return false;
    }
  }
  return String(value ?? "").trim() !== "";
}

/** Primer bloque obligatorio sin responder dentro de `blocks` (incluye columnas), o null. */
function firstMissing(blocks, respuestas) {
  return flattenBlocks(blocks).find((b) => b.obligatorio && OBLIGATORIABLES.includes(b.tipo) && !isAnswered(b, respuestas[b.id])) || null;
}

const Req = ({ b }) => (b.obligatorio ? <span className="text-destructive" aria-hidden> *</span> : null);

function getApiErrorMessage(err, fallback = "Error al enviar") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (typeof first === "string" && first.trim()) return first;
    if (first && typeof first === "object" && typeof first.msg === "string" && first.msg.trim()) {
      return first.msg;
    }
    return fallback;
  }
  if (detail && typeof detail === "object" && typeof detail.msg === "string" && detail.msg.trim()) {
    return detail.msg;
  }
  return err?.message || fallback;
}

function BlockView({ block, respuestas, onInputChange, onCarouselImageClick }) {
  const b = block;
  if (!b || !b.tipo) return null;

  if (b.tipo === "titulo") {
    const n = Math.min(6, Math.max(1, b.nivel ?? 1));
    const Tag = `h${n}`;
    const cls = n === 6 ? "text-sm font-medium scroll-mt-6" : "font-semibold scroll-mt-6";
    return <Tag className={cls}>{b.texto || "\u00A0"}</Tag>;
  }

  if (b.tipo === "parrafo") {
    return <p className="text-muted-foreground whitespace-pre-wrap">{b.texto || "\u00A0"}</p>;
  }

  if (b.tipo === "columnas") {
    const colsCount = b.columnas === 3 ? 3 : 2;
    const cols = Array.isArray(b.columnas_bloques) ? b.columnas_bloques.slice(0, colsCount) : [];
    while (cols.length < colsCount) cols.push([]);
    return (
      <div className={`grid gap-4 ${colsCount === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        {cols.map((colBlocks, colIdx) => (
          <div key={colIdx} className="space-y-4">
            {(Array.isArray(colBlocks) ? colBlocks : []).map((inner) => (
              <BlockView
                key={inner.id}
                block={inner}
                respuestas={respuestas}
                onInputChange={onInputChange}
                onCarouselImageClick={onCarouselImageClick}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (b.tipo === "area_texto") {
    return (
      <div className="space-y-1">
        {b.titulo && (
          <Label htmlFor={b.id} className="font-medium">
            {b.titulo}<Req b={b} />
          </Label>
        )}
        <textarea
          id={b.id}
          placeholder={b.placeholder || ""}
          value={respuestas[b.id] ?? ""}
          onChange={(e) => onInputChange(b.id, e.target.value)}
          className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </div>
    );
  }

  if (b.tipo === "imagen" && b.url) {
    return (
      <button
        type="button"
        onClick={() => onCarouselImageClick?.([b.url], 0)}
        className="w-full rounded-lg border overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring aspect-square"
      >
        <img src={b.url} alt="" className="w-full h-full object-cover cursor-pointer hover:opacity-90" />
      </button>
    );
  }

  if (b.tipo === "carousel") {
    const imgs = (b.imagenes || []).filter((img) => img?.url).map((img) => img.url);
    if (imgs.length === 0) return null;
    return (
      <div className="grid grid-cols-3 gap-2 py-2">
        {imgs.map((url, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onCarouselImageClick?.(imgs, i)}
            className="aspect-square rounded-lg border overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <img src={url} alt="" className="w-full h-full object-cover cursor-pointer hover:opacity-90" />
          </button>
        ))}
      </div>
    );
  }

  if (b.tipo === "input") {
    return (
      <div className="space-y-1">
        {b.titulo && <Label htmlFor={b.id}>{b.titulo}<Req b={b} /></Label>}
        <Input
          id={b.id}
          placeholder={b.placeholder || ""}
          value={respuestas[b.id] ?? ""}
          onChange={(e) => onInputChange(b.id, e.target.value)}
          className="h-11 sm:h-9"
        />
      </div>
    );
  }

  if (b.tipo === "archivo") {
    let currentFiles = [];
    try {
      const parsed = JSON.parse(respuestas[b.id] || "[]");
      if (Array.isArray(parsed)) currentFiles = parsed;
    } catch (_) {}
    return (
      <div className="space-y-2">
        {b.titulo && <Label htmlFor={b.id}>{b.titulo}<Req b={b} /></Label>}
        <Input
          id={b.id}
          type="file"
          multiple
          accept=".png,.jpg,.jpeg,.webp,.pdf,image/png,image/jpeg,image/webp,application/pdf"
          onChange={async (e) => {
            const files = Array.from(e.target.files || []);
            if (!files.length) return;
            try {
              const payload = await Promise.all(
                files.map((file) =>
                  readFileAsDataUrlAny(file).then((url) => ({ name: file.name, type: file.type, url }))
                )
              );
              const merged = [...currentFiles, ...payload];
              onInputChange(b.id, JSON.stringify(merged));
            } catch (_) {
              toast.error("Error al leer archivos");
            } finally {
              e.target.value = "";
            }
          }}
        />
        {currentFiles.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {currentFiles.map((f, i) => {
              const isImage = typeof f?.type === "string" && f.type.startsWith("image/");
              return (
                <div
                  key={`${f?.name || "archivo"}-${i}`}
                  className="relative inline-flex items-center gap-2 rounded-md border bg-muted/50 px-2.5 py-1.5 pr-8 text-xs"
                >
                  {isImage ? <img src={f.url} alt={f.name || ""} className="h-8 w-8 rounded object-cover border" /> : null}
                  <span className="max-w-[220px] truncate">{f?.name || `archivo-${i + 1}`}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const next = currentFiles.filter((_, idx) => idx !== i);
                      onInputChange(b.id, next.length ? JSON.stringify(next) : "");
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
                    aria-label={`Quitar ${f?.name || "archivo"}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (b.tipo === "opciones_checkbox") {
    const opciones = (b.opciones || []).filter((o) => (o?.label || "").trim());
    let selected = [];
    try {
      const raw = respuestas[b.id];
      if (typeof raw === "string" && raw) selected = JSON.parse(raw);
      if (!Array.isArray(selected)) selected = [];
    } catch (_) {}
    const toggle = (label) => {
      const next = selected.includes(label) ? selected.filter((l) => l !== label) : [...selected, label];
      onInputChange(b.id, JSON.stringify(next));
    };
    return (
      <div className="space-y-2">
        {b.titulo && <Label className="font-medium">{b.titulo}<Req b={b} /></Label>}
        <div className="flex flex-col gap-2">
          {opciones.map((o, i) => (
            <label key={i} className="flex items-center gap-3 sm:gap-2 min-h-11 sm:min-h-0 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(o.label)}
                onChange={() => toggle(o.label)}
                className="h-5 w-5 sm:h-4 sm:w-4 shrink-0 rounded border-input"
              />
              <span className="text-sm">{o.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (b.tipo === "desplegable") {
    const opciones = (b.opciones || []).filter((o) => (o?.label || "").trim());
    const value = respuestas[b.id] ?? "";
    return (
      <div className="space-y-2">
        {b.titulo && <Label htmlFor={b.id} className="font-medium">{b.titulo}<Req b={b} /></Label>}
        <select
          id={b.id}
          value={value}
          onChange={(e) => onInputChange(b.id, e.target.value)}
          className="flex h-11 sm:h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">Selecciona...</option>
          {opciones.map((o, i) => (
            <option key={i} value={o.label}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }

  if (b.tipo === "consentimiento_privacidad") {
    const checked = Boolean(respuestas[b.id]);
    const texto = (b.titulo || "").trim() || "He leído y acepto la";
    const textoEnlace = (b.texto_enlace || "").trim() || "política de privacidad";
    const href = (b.url_politica || "").trim();
    return (
      <div className="space-y-1">
        <label htmlFor={b.id} className="flex items-start gap-2 cursor-pointer text-sm">
          <input
            id={b.id}
            type="checkbox"
            checked={checked}
            onChange={(e) => onInputChange(b.id, e.target.checked)}
            className="mt-0.5 h-5 w-5 sm:h-4 sm:w-4 shrink-0 rounded border-input"
          />
          <span>
            {texto}{" "}
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
                onClick={(e) => e.stopPropagation()}
              >
                {textoEnlace}
              </a>
            ) : (
              <span className="underline underline-offset-2">{textoEnlace}</span>
            )}
          </span>
        </label>
      </div>
    );
  }

  if (b.tipo === "paginacion") return null;

  if (b.tipo === "enlace") {
    const url = b.url || "#";
    const text = b.texto || url;
    return (
      <p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2"
        >
          {text}
        </a>
      </p>
    );
  }

  if (b.tipo === "enlaces_recursos") {
    const items = b.items || [];
    return (
      <div className="space-y-1">
        <p className="text-sm font-medium">Enlaces y recursos</p>
        <div className="flex flex-wrap gap-2">
          {items.map((item, i) => {
            if (item.tipo === "url" && (item.url || item.texto)) {
              return (
                <a
                  key={i}
                  href={item.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-md border bg-muted/50 px-3 py-1.5 text-sm text-foreground hover:bg-muted"
                >
                  {item.texto || item.url}
                </a>
              );
            }
            if (item.tipo === "archivo" && (item.url || item.nombre_archivo)) {
              const label = item.nombre_archivo || "Descargar";
              if (item.url) {
                return (
                  <a
                    key={i}
                    href={item.url}
                    download={item.nombre_archivo}
                    className="inline-flex items-center rounded-md border bg-muted/50 px-3 py-1.5 text-sm text-foreground hover:bg-muted"
                  >
                    {label}
                  </a>
                );
              }
              return (
                <span
                  key={i}
                  className="inline-flex items-center rounded-md border bg-muted/50 px-3 py-1.5 text-sm"
                >
                  {label}
                </span>
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  }

  if (b.tipo === "divisor") {
    const arriba = Math.max(0, parseInt(b.altura_arriba, 10) || 16);
    const abajo = Math.max(0, parseInt(b.altura_abajo, 10) || 16);
    const grosor = Math.max(0, Math.min(4, parseInt(b.grosor, 10) ?? 1));
    const estilo = b.estilo === "dashed" ? "dashed" : b.estilo === "dotted" ? "dotted" : "solid";
    return (
      <div
        className="border-t border-gray-300"
        style={{
          marginTop: `${arriba}px`,
          marginBottom: `${abajo}px`,
          borderTopWidth: grosor === 0 ? 0 : `${grosor}px`,
          borderTopStyle: grosor === 0 ? "none" : estilo,
        }}
        aria-hidden
      />
    );
  }

  return null;
}

export default function RealizarFormularioPage() {
  const { slug } = useParams();
  const [formulario, setFormulario] = useState(null);
  const isTouch = useIsTouch();
  const [loading, setLoading] = useState(true);
  const [respuestas, setRespuestas] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [lightboxUrls, setLightboxUrls] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    const siteKey = getSiteKey();
    if (!siteKey) {
      setRecaptchaReady(true);
    } else {
      loadRecaptchaScript().then(() => {
        if (mounted.current) setRecaptchaReady(true);
      });
    }
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    getFormularioBySlug(slug)
      .then((data) => {
        setFormulario(data);
        setRespuestas({});
        setCurrentPage(0);
      })
      .catch(() => {
        toast.error("Formulario no encontrado");
        setFormulario(null);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const handleInputChange = (blockId, value) => {
    setRespuestas((prev) => ({ ...prev, [blockId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!slug || submitting || submitted) return;
    const consentBlocks = flattenBlocks(formulario?.bloques || []).filter((b) => b?.tipo === "consentimiento_privacidad");
    for (const c of consentBlocks) {
      if (!respuestas[c.id]) {
        toast.error("Debes aceptar la política de privacidad para continuar");
        return;
      }
    }
    const missing = firstMissing(formulario?.bloques || [], respuestas);
    if (missing) {
      const pageIdx = pagesOfBlocks(formulario?.bloques || []).findIndex((pg) => flattenBlocks(pg).some((x) => x.id === missing.id));
      if (pageIdx >= 0) setCurrentPage(pageIdx);
      toast.error(`Responde a la pregunta obligatoria${missing.titulo ? `: «${missing.titulo}»` : ""}`);
      return;
    }
    let recaptchaToken = null;
    if (getSiteKey()) {
      try {
        recaptchaToken = await getRecaptchaToken("submit_formulario");
      } catch (err) {
        console.error(err);
        toast.error("Error al verificar el captcha. Recarga la página e inténtalo de nuevo.");
        return;
      }
    }
    setSubmitting(true);
    try {
      await submitFormulario(slug, respuestas, recaptchaToken);
      setSubmitted(true);
      toast.success("Respuestas enviadas correctamente");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Error al enviar"));
    } finally {
      setSubmitting(false);
    }
  };

  const onCarouselImageClick = (urls, index) => {
    setLightboxUrls(urls || []);
    setLightboxIndex(index || 0);
  };

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  if (!formulario) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background px-4">
        <p className="text-muted-foreground">Formulario no encontrado.</p>
      </div>
    );
  }

  const emoji = formulario.emoji || EMOJI_PREDETERMINADO;
  const bloques = formulario.bloques || [];
  const imagenCabecera = formulario.imagen_cabecera || "";
  const imagenFondo = formulario.imagen_fondo || "";
  // background-attachment: fixed no funciona en iOS/Android (amplía la imagen a toda la página)
  const bgStyle = imagenFondo
    ? {
        backgroundImage: `url(${imagenFondo})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: isTouch ? "scroll" : "fixed",
      }
    : undefined;

  const paginas = (() => {
    const out = [];
    let current = [];
    for (const b of bloques) {
      if (b.tipo === "paginacion") {
        if (current.length) out.push(current);
        current = [];
      } else current.push(b);
    }
    if (current.length) out.push(current);
    return out.length ? out : [[]];
  })();

  const paginaActual = paginas[currentPage] || [];
  const isLastPage = currentPage >= paginas.length - 1;

  if (submitted) {
    return (
      <div
        className="min-h-dvh bg-background flex items-center justify-center px-4"
        style={
          bgStyle
        }
      >
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-xl font-semibold">Respuestas enviadas</h1>
          <p className="text-muted-foreground">
            Gracias. Tus respuestas se han guardado correctamente.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-dvh bg-background"
      style={
        bgStyle
      }
    >
      <main className="max-w-2xl mx-auto px-3 sm:px-6 py-4 sm:py-10 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:pb-10">
        <div className="rounded-2xl border border-gray-300 bg-white min-h-[50vh] px-4 sm:px-6 py-6 sm:py-8">
          {(imagenCabecera || emoji || EMOJI_PREDETERMINADO) && (
            <>
              <div className="relative z-0 w-full h-[160px] sm:h-[220px] rounded-lg border overflow-hidden bg-muted/30">
                {imagenCabecera && (
                  <img src={imagenCabecera} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="relative z-10 -mt-10 mb-10 pl-4 w-fit">
                <span
                  className="text-7xl leading-none select-none inline-block"
                  style={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                  aria-hidden
                >
                  {emoji}
                </span>
              </div>
            </>
          )}
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-6 sm:mb-8 break-words">{formulario.titulo || "Formulario"}</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {paginaActual.map((b) => (
              <BlockView
                key={b.id}
                block={b}
                respuestas={respuestas}
                onInputChange={handleInputChange}
                onCarouselImageClick={onCarouselImageClick}
              />
            ))}

            <div className="flex items-center gap-2 pt-4 [&>button]:h-11 [&>button]:flex-1 sm:[&>button]:h-9 sm:[&>button]:flex-none">
              {currentPage > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="gap-2"
                >
                  <ChevronLeft className="w-4 h-4" /> Atrás
                </Button>
              )}
              {!isLastPage && (
                <Button
                  type="button"
                  onClick={() => {
                    const missing = firstMissing(paginaActual, respuestas);
                    if (missing) {
                      toast.error(`Responde a la pregunta obligatoria${missing.titulo ? `: «${missing.titulo}»` : ""}`);
                      return;
                    }
                    setCurrentPage((p) => p + 1);
                  }}
                  className="gap-2"
                >
                  Siguiente <ChevronRight className="w-4 h-4" />
                </Button>
              )}
              {isLastPage && (
                <Button type="submit" disabled={!recaptchaReady || submitting || submitted} className="gap-2">
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                  ) : (
                    <Send className="w-4 h-4" strokeWidth={1.5} aria-hidden />
                  )}
                  {submitted ? "Enviado" : "Enviar respuestas"}
                </Button>
              )}
            </div>
          </form>
        </div>
      </main>

      <Dialog open={lightboxUrls.length > 0} onOpenChange={(open) => !open && setLightboxUrls([])}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto p-0 overflow-hidden flex flex-col items-center">
          {lightboxUrls[lightboxIndex] && (
            <img src={lightboxUrls[lightboxIndex]} alt="" className="w-full h-auto max-h-[85vh] object-contain" />
          )}
          {lightboxUrls.length > 1 && (
            <div className="flex items-center justify-between w-full px-4 py-2 border-t bg-background">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setLightboxIndex((i) => (i <= 0 ? lightboxUrls.length - 1 : i - 1))}
                aria-label="Anterior"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
              </Button>
              <span className="text-sm text-muted-foreground">
                {lightboxIndex + 1} / {lightboxUrls.length}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setLightboxIndex((i) => (i >= lightboxUrls.length - 1 ? 0 : i + 1))}
                aria-label="Siguiente"
              >
                <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

